import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { REFUSED_MATCH_STATUSES } from "@/components/jobs/kanban/types/index";
import { useKanbanColumns } from "@/hooks/useKanbanColumns";

interface MassUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedMatchIds: string[];
  onUpdate: () => void;
}

export const MassUpdateModal = ({ 
  isOpen, 
  onClose, 
  selectedMatchIds, 
  onUpdate 
}: MassUpdateModalProps) => {
  const { t } = useTranslation();
  const { stages, getStatusLabel } = useKanbanColumns();
  const [fieldValue, setFieldValue] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState(false);

  const matchStatusOptions = [
    ...stages.filter(stage => stage.id !== 'refused').map(stage => stage.id),
    ...REFUSED_MATCH_STATUSES
  ].map(status => ({
    value: status,
    label: getStatusLabel(status)
  }));

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFieldValue("");
    }
  }, [isOpen]);

  const handleUpdate = async () => {
    if (fieldValue === "" || selectedMatchIds.length === 0) {
      toast({
        title: "Error",
        description: "Please select a status",
        variant: "destructive"
      });
      return;
    }

    setIsUpdating(true);

    try {
      // Prepare the update data
      const updateData = { status: fieldValue };

      // Perform batch update
      const { error } = await supabase
        .from('matches')
        .update(updateData)
        .in('id', selectedMatchIds);

      if (error) throw error;

      // Create notifications for status changes
        try {
          // Get match details for notifications
          const { data: matchesData } = await supabase
            .from('matches')
            .select(`
              id,
              jobs(title, organization_uuid),
              candidates(name)
            `)
            .in('id', selectedMatchIds);

          if (matchesData) {
            // Create notifications for each match
            for (const match of matchesData) {
              if (match.jobs && match.candidates) {
                try {
                  await supabase.rpc('create_org_notifications_with_subscriptions', {
                    p_organization_uuid: match.jobs.organization_uuid,
                    p_type: 'application_status_change',
                    p_title: `Bulk Status Update: ${match.candidates.name} → ${fieldValue}`,
                    p_message: `${match.candidates.name} has been moved to "${fieldValue}" for the position "${match.jobs.title}" via bulk update.`,
                    p_data: {
                      entity_type: 'match',
                      entity_id: match.id,
                      job_title: match.jobs.title,
                      candidate_name: match.candidates.name,
                      new_status: fieldValue,
                      action: 'bulk_status_update'
                    },
                    p_entity_type: 'match',
                    p_entity_id: match.id
                  });
                } catch (notificationError) {
                  console.warn('Failed to create notification for match:', match.id, notificationError);
                }
              }
            }
          }
        } catch (notificationError) {
          console.warn('Failed to create notifications for bulk update:', notificationError);
        }

      toast({
        title: "Success",
        description: `Successfully updated ${selectedMatchIds.length} match(es)`,
      });

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating matches:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update matches",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Mass Update</DialogTitle>
          <DialogDescription>
            Update fields for {selectedMatchIds.length} selected match(es)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="field-select">Field to Update</Label>
            <Select value="status" disabled>
              <SelectTrigger id="field-select" className="bg-gray-50 dark:bg-gray-800/50">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="status">Status</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="status-select">New Value</Label>
            <Select value={fieldValue} onValueChange={setFieldValue}>
              <SelectTrigger id="status-select">
                <SelectValue placeholder="Select new status" />
              </SelectTrigger>
              <SelectContent>
                  {matchStatusOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdate} 
            disabled={fieldValue === "" || isUpdating}
          >
            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Update {selectedMatchIds.length} Match(es)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
